import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, Observable } from 'rxjs';

import { REACH_API_COMMON_HEADERS } from '~app-client/api/tokens';

import { throwAsApiError } from '../utils/error';
import { genericRetryStrategy, GenericRetryStrategyOptions } from '../utils/generic-retry.operator';

export type ApiParams = HttpParams | Record<string, string | string[]>;

@Injectable({ providedIn: 'root' })
export class ApiHttpClientService {
	protected readonly http = inject(HttpClient);
	protected readonly commonHeaders = inject(REACH_API_COMMON_HEADERS);

	public getObs<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string
	): Observable<T> {
		const options = {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
			responseType: responseType as any,
		};
		return this.http.get<T>(path, options).pipe(catchError(throwAsApiError));
	}

	public async get<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string
	): Promise<T> {
		return await firstValueFrom(this.getObs<T>(path, params, headers, responseType));
	}

	public async getWithHeaders<T>(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<HttpResponse<T>> {
		const responseType: 'json' = 'json' as const;
		const observe: 'response' = 'response' as const;

		const options = {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			responseType,
			observe,
		};
		return await firstValueFrom(
			this.http.get(path, options) as Observable<HttpResponse<T>>
		).catch(throwAsApiError);
	}

	public async getBlobWithHeaders(
		path: string,
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<HttpResponse<Blob>> {
		const responseType: 'blob' = 'blob' as const;
		const observe: 'response' = 'response' as const;

		const options = {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			responseType,
			observe,
		};
		return await firstValueFrom(this.http.get(path, options)).catch(throwAsApiError);
	}

	public async post<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>,
		responseType?: string,
		retryOptions?: Partial<GenericRetryStrategyOptions>
	): Promise<T> {
		const request$ = this.http.post<T>(path, body, {
			params,
			headers: {
				...this.commonHeaders,
				...headers,
			},
			withCredentials: true,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
			responseType: responseType as any,
		});
		const withRetry$ = retryOptions
			? request$.pipe(genericRetryStrategy(retryOptions))
			: request$;
		return await firstValueFrom(withRetry$).catch(throwAsApiError);
	}

	public async patch<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.patch<T>(path, body, {
				params,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		).catch(throwAsApiError);
	}

	public async put<T>(
		path: string,
		body: unknown = {},
		params?: ApiParams,
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.put<T>(path, body, {
				params,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		).catch(throwAsApiError);
	}

	public async delete<T>(
		path: string,
		body: unknown = {},
		headers?: Record<string, string>
	): Promise<T> {
		return await firstValueFrom(
			this.http.delete<T>(path, {
				body,
				headers: {
					...this.commonHeaders,
					...headers,
				},
				withCredentials: true,
			})
		).catch(throwAsApiError);
	}
}
